import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Bug, Upload, X } from 'lucide-react';
import { Button } from '../../tailwind/button';
import { Input } from '../../tailwind/input';
import { Label } from '../../tailwind/label';
import { Textarea } from '../../tailwind/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../tailwind/dialog';
import { submitReportRequest, closeReportDialog, setReportFormData } from '../../../store/reportSlice';

const ReportIssueDialog = () => {
  const dispatch = useDispatch();
  const { isDialogOpen, formData, isSubmitting } = useSelector(state => state.report);
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDialogClose = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    dispatch(closeReportDialog());
  };

  const handleDialogOpenChange = (open) => {
    if (!open) {
      handleDialogClose();
    }
  };

  const handleFormInputChange = (e) => {
    const { id, value } = e.target;
    dispatch(setReportFormData({
      [id]: value
    }));
  };

  const handleUploadAreaClick = (e) => {
    if (e.target === fileInputRef.current) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('File size exceeds 10MB limit');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.issueTitle) {
      alert('Please fill in issue title');
      return;
    }

    dispatch(submitReportRequest({
      issueTitle: formData.issueTitle,
      details: formData.details,
      file: selectedFile
    }));
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-red-500" />
              Report a Bug
            </DialogTitle>
            <DialogDescription>
              Found an issue? Let us know so we can fix it. Or email us directly at <a href="mailto:support@prodyt.com" className="text-blue-600 hover:underline">support@prodyt.com</a>
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="issueTitle">Issue Title</Label>
              <Input
                id="issueTitle"
                placeholder="Brief description of the problem"
                required
                value={formData.issueTitle}
                onChange={handleFormInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="details">Details</Label>
              <Textarea
                id="details"
                placeholder="What were you trying to do? What went wrong?"
                className="h-24"
                value={formData.details}
                onChange={handleFormInputChange}
              />
            </div>

            <div className="space-y-2">
              <Label>Attachment (Optional)</Label>
              <div
                className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-md hover:bg-gray-50 transition-colors relative cursor-pointer group"
                onClick={handleUploadAreaClick}
              >
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  <div className="flex text-sm text-gray-600">
                    <span className="font-medium text-blue-600 hover:text-blue-500">Upload a file</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/*,video/*"
                      ref={fileInputRef}
                      onClick={(e) => e.stopPropagation()}
                      onChange={handleFileChange}
                    />
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PNG, JPG, MP4 up to 10MB</p>
                </div>
              </div>
              {selectedFile && (
                <div className="flex items-center justify-between bg-blue-50 text-blue-700 px-3 py-2 rounded-md text-sm mt-2 border border-blue-100">
                  <span className="truncate flex-1">{selectedFile.name}</span>
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    className="text-blue-500 hover:text-blue-800 hover:bg-blue-100 p-1.5 rounded-md transition-colors ml-2 flex-shrink-0"
                    title="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleDialogClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssueDialog;
